import { useState } from 'react';
import { createFileRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { SidebarLayout } from '../components/Sidebar/SidebarLayout';
import { TestProvider, useTestContext } from '../context/TestContext';
import OnboardingSelectTest from '../components/Onboarding/OnboardingSelectTest';
import WelcomeScreen from '../components/Onboarding/WelcomeScreen';
import WelcomeModal from '../components/Onboarding/WelcomeModal';
import OnboardingFlow from '../onboarding/OnboardingFlow';
import Loader from '../components/Loader/Loader';
import type { OnboardingFormData } from '../onboarding/hooks/useOnboardingForm';
import IndigoAIChatWidget from '../components/IndigoAI/IndigoAIChatWidget';

const INDIGO_ENABLED = import.meta.env.VITE_INDIGO_ENABLED === 'true';

function isExecutionRoute(pathname: string): boolean {
  return (
    (/^\/simulazioni\/[^/]+$/.test(pathname) ||
      /^\/esercitazioni\/[^/]+$/.test(pathname)) &&
    !pathname.endsWith('/correzione')
  );
}

export const Route = createFileRoute('/_sidebar')({
  component: SidebarLayoutRoute,
});

function SidebarLayoutRoute() {
  return (
    <TestProvider>
      <SidebarLayoutInner />
    </TestProvider>
  );
}

function SidebarLayoutInner() {
  const { isFetching, needsProfiling, needsOnboarding } = useTestContext();
  const currentPath = useRouterState({ select: (s) => s.location.pathname });
  const [profilingDone, setProfilingDone] = useState(false);
  const [modalDone, setModalDone] = useState(false);
  const [welcomeDone, setWelcomeDone] = useState(false);

  const isOnboarding =
    isFetching ||
    (needsProfiling && !profilingDone) ||
    needsOnboarding ||
    (profilingDone && !welcomeDone);

  let content: React.ReactNode;

  if (isFetching) {
    content = <Loader fullScreen />;
  } else if (needsProfiling && !profilingDone) {
    content = (
      <OnboardingFlow onComplete={(_data: OnboardingFormData) => setProfilingDone(true)} />
    );
  } else if (needsOnboarding) {
    content = <OnboardingSelectTest />;
  } else if (profilingDone && !modalDone) {
    content = (
      <SidebarLayout>
        <Outlet />
        <WelcomeModal onConfirm={() => setModalDone(true)} />
      </SidebarLayout>
    );
  } else if (profilingDone && !welcomeDone) {
    content = <WelcomeScreen onDone={() => setWelcomeDone(true)} />;
  } else {
    content = (
      <SidebarLayout>
        <Outlet />
      </SidebarLayout>
    );
  }

  return (
    <>
      {content}
      {INDIGO_ENABLED && !isOnboarding && (
        <IndigoAIChatWidget
          variables={{}}
          autoOpen={false}
          visible={!isExecutionRoute(currentPath)}
        />
      )}
    </>
  );
}
